import { useState } from 'react';
import type { ReactNode } from 'react';
import { ContextKindKeyBadge } from '@gonfalon/contexts/components/ContextKindKeyBadge';
import { getDisplayDurationFromMs } from '@gonfalon/format/getDisplayDurationFromMs';
import { pluralize } from '@gonfalon/format/pluralize';
import { Button, Tag, TagGroup, TagList } from '@launchpad-ui/components';

import styles from './MultiPhaseAutomatedReleaseDescription.module.css';

interface MultiPhaseReleaseStage {
  readonly rolloutWeight: number;
  readonly monitoringWindowMilliseconds?: number;
}

interface MultiPhaseReleasePhase {
  /** Audience description shown as the card label; falls back to "Phase {n}" when absent. */
  readonly description?: string;
  readonly clauses: ReactNode;
  readonly stages: readonly MultiPhaseReleaseStage[];
}

interface MultiPhaseReleaseMetric {
  readonly key: string;
  readonly autoRollback: boolean;
}

export interface MultiPhaseAutomatedReleaseDescriptionProps {
  readonly phases: readonly MultiPhaseReleasePhase[];
  readonly originalVariationId: string;
  readonly targetVariationId: string;
  readonly rolloutContextKind: string;
  /** Individual (non-group) metrics only; the caller filters out group metrics. */
  readonly metrics?: readonly MultiPhaseReleaseMetric[];
  readonly updateDefaultRuleOnCompletion?: boolean;
  readonly renderVariation: (variationId: string) => ReactNode;
}

export function MultiPhaseAutomatedReleaseDescription({
  phases,
  originalVariationId,
  targetVariationId,
  rolloutContextKind,
  metrics,
  updateDefaultRuleOnCompletion,
  renderVariation,
}: MultiPhaseAutomatedReleaseDescriptionProps) {
  return (
    <div>
      Add phased release{' '}
      <span className={styles.phaseCount}>
        {phases.length} {pluralize(phases.length, 'phase', 'phases')}
      </span>
      <div className={styles.phasesContainer}>
        {phases.map((phase, index) => (
          <PhaseCard key={index} phase={phase} phaseNumber={index + 1} />
        ))}
        <SharedAcrossAllPhases
          originalVariationId={originalVariationId}
          targetVariationId={targetVariationId}
          rolloutContextKind={rolloutContextKind}
          metrics={metrics}
          updateDefaultRuleOnCompletion={updateDefaultRuleOnCompletion}
          renderVariation={renderVariation}
        />
      </div>
    </div>
  );
}

function PhaseCard({ phase, phaseNumber }: { phase: MultiPhaseReleasePhase; phaseNumber: number }) {
  const description = phase.description?.trim();
  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <span className={styles.phaseLabel}>{description || `Phase ${phaseNumber}`}</span>
        <span className={styles.phaseAudience}>{phase.clauses}</span>
      </div>
      <RolloutDurationRow stages={phase.stages} />
    </div>
  );
}

function SharedAcrossAllPhases({
  originalVariationId,
  targetVariationId,
  rolloutContextKind,
  metrics,
  updateDefaultRuleOnCompletion,
  renderVariation,
}: Omit<MultiPhaseAutomatedReleaseDescriptionProps, 'phases'>) {
  return (
    <div className={styles.sharedSection}>
      <span className={styles.sharedHeading}>Shared across all phases</span>
      <div className={styles.container}>
        <RolloutRow label="Rollout:">
          {renderVariation(targetVariationId)} by <ContextKindKeyBadge contextKind={{ key: rolloutContextKind }} />
        </RolloutRow>
        <RolloutRow label="Original variation:">{renderVariation(originalVariationId)}</RolloutRow>
        <MetricsRow metrics={metrics} />
        <CleanupPhaseRow
          updateDefaultRuleOnCompletion={updateDefaultRuleOnCompletion}
          targetVariationId={targetVariationId}
          renderVariation={renderVariation}
        />
      </div>
    </div>
  );
}

function CleanupPhaseRow({
  updateDefaultRuleOnCompletion,
  targetVariationId,
  renderVariation,
}: {
  updateDefaultRuleOnCompletion: boolean | undefined;
  targetVariationId: string;
  renderVariation: (variationId: string) => ReactNode;
}) {
  if (updateDefaultRuleOnCompletion === undefined) {
    return null;
  }
  return (
    <RolloutRow label="Cleanup phase:">
      {updateDefaultRuleOnCompletion ? (
        <>Remove rules and update default rule to serve {renderVariation(targetVariationId)}</>
      ) : (
        'Rules remain in place'
      )}
    </RolloutRow>
  );
}

function RolloutRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{children}</span>
    </div>
  );
}

/** Renders the "Rollout duration" row with a stage-count chip and an expandable per-stage table. A synthetic 100% terminal stage is appended since phased releases always end at full rollout. Returns null when the phase has no stages. */
function RolloutDurationRow({ stages }: { stages: readonly MultiPhaseReleaseStage[] }) {
  const [showStages, setShowStages] = useState(false);
  if (stages.length === 0) {
    return null;
  }
  const totalDurationMs = stages.reduce((sum, stage) => sum + (stage.monitoringWindowMilliseconds ?? 0), 0);
  const tableStages = [...stages, { rolloutWeight: 100000 }];
  const stageLabel = pluralize(tableStages.length, 'step', 'steps');

  return (
    <>
      <RolloutRow label="Rollout duration:">
        {getDisplayDurationFromMs(totalDurationMs)}
        <TagGroup aria-label="Step count">
          <TagList>
            <Tag size="small" textValue={`${tableStages.length} ${stageLabel}`}>
              {tableStages.length} {stageLabel}
            </Tag>
          </TagList>
        </TagGroup>
        <Button variant="minimal" size="small" onPress={() => setShowStages((prev) => !prev)}>
          {showStages ? 'Hide details' : 'Show details'}
        </Button>
      </RolloutRow>
      {showStages && <StagesTable stages={tableStages} />}
    </>
  );
}

/** Renders the "Metrics" row with an individual-metric count and an expandable per-metric auto-rollback table. Returns null when there are no metrics. */
function MetricsRow({ metrics }: { metrics?: readonly MultiPhaseReleaseMetric[] }) {
  const [showMetrics, setShowMetrics] = useState(false);
  if (metrics === undefined || metrics.length === 0) {
    return null;
  }

  return (
    <>
      <RolloutRow label="Metrics:">
        {metrics.length} {pluralize(metrics.length, 'metric', 'metrics')}
        <Button variant="minimal" size="small" onPress={() => setShowMetrics((prev) => !prev)}>
          {showMetrics ? 'Hide details' : 'Show details'}
        </Button>
      </RolloutRow>
      {showMetrics && <MetricsTable metrics={metrics} />}
    </>
  );
}

function StagesTable({ stages }: { stages: readonly MultiPhaseReleaseStage[] }) {
  return (
    <table className={styles.detailsTable}>
      <thead>
        <tr>
          <th>Step</th>
          <th>Weight</th>
          <th>Duration</th>
        </tr>
      </thead>
      <tbody>
        {stages.map((stage, i) => (
          <tr key={i}>
            <td>{i + 1}</td>
            <td>{stage.rolloutWeight / 1000}%</td>
            <td>
              {stage.monitoringWindowMilliseconds !== undefined
                ? getDisplayDurationFromMs(stage.monitoringWindowMilliseconds, { format: 'short' })
                : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MetricsTable({ metrics }: { metrics: readonly MultiPhaseReleaseMetric[] }) {
  return (
    <table className={styles.detailsTable}>
      <thead>
        <tr>
          <th>Metric</th>
          <th>Auto rollback</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map((m) => (
          <tr key={m.key}>
            <td>{m.key}</td>
            <td>{m.autoRollback ? 'Yes' : 'No'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
